import {
  Component,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  MultimediaIcon,
  MultimediaList,
  MultimediaListItem,
} from '../../../shared/interfaces/multimedia-list.interface';
import { IconComponent } from '../../atoms/icon/icon.component';
import { BadgeComponent } from '../../atoms/badge/badge.component';
import { TooltipComponent } from '../../molecules/tooltip/tooltip.component';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import BadgeType from '../../../shared/enums/badge-type.enums';
import { ModalType } from '../../../shared/enums/modal-type.enums';

import { Tooltip } from '../../../shared/interfaces/tooltip.interface';

import {
  FaIconLibrary,
  IconName,
  IconPrefix,
} from '@fortawesome/angular-fontawesome';
import { Subscription } from 'rxjs';
import { CapitalizePipe } from '../../capitalize.pipe';
import {
  CaasMultimediaListLink,
  CaasComponent,
} from '../../../shared/interfaces/caas/caas.interface';
import { ButtonComponent } from '../../molecules/button/button.component';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { safeAssign } from '../../../shared/utils/safe-assign.util';

@Component({
  selector: 'hdi-multimedia-list',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    BadgeComponent,
    TooltipComponent,
    RouterModule,
    CapitalizePipe,
    ButtonComponent,
  ],
  templateUrl: './multimedia-list.component.html',
  styleUrls: ['./multimedia-list.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class MultimediaListComponent implements OnDestroy, OnChanges {
  @Input() multimediaList: MultimediaList;
  @Input() backgroundColor: 'white' | 'grey' = 'white';
  @Input() caasData?: CaasComponent;
  @Input() sectionBg?: 'white' | 'grey';
  private readonly modalVideoSubscription$: Subscription = new Subscription();
  showModal: boolean = false;
  tooltip: Tooltip;
  clipboardTooltip?: Tooltip;

  Sizes = Sizes;
  Colors = Colors;
  BadgeType = BadgeType;
  ModalType = ModalType;
  ButtonType = ButtonType;

  constructor(
    private readonly library: FaIconLibrary,
    private readonly caasRoutesService: CaasRoutesService,
    private readonly router: Router,
    private readonly errorHandler: ErrorHandlerService
  ) {
    this.multimediaList = {
      title: '',
      key: '',
      items: [],
    };
    this.tooltip = {
      id: '',
      description: '',
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData'] && this.caasData) {
      this.multimediaList = this.setCaasContent();
      this.backgroundColor = this.sectionBg === 'grey' ? 'grey' : 'white';
    }
  }

  private setCaasContent(): MultimediaList {
    const multimediaList: MultimediaList = {
      title: '',
      key: '',
      items: [],
    };

    safeAssign(
      () => {
        multimediaList.title = this.caasData?.formData?.st_header?.value ?? '';
      },
      'Set MultimediaList Title',
      this.errorHandler
    );

    safeAssign(
      () => {
        multimediaList.key = this.caasData?.identifier ?? '';
      },
      'Set MultimediaList Key',
      this.errorHandler
    );

    safeAssign(
      () => {
        const links = this.caasData?.formData?.st_links?.value ?? [];
        multimediaList.items = links.map((link, idx) => {
          const item: MultimediaListItem = {
            label: '',
            title: '',
            link: '',
            leftIcon: '',
            badge: '',
            rightIcons: [],
          };
          safeAssign(
            () => {
              item.label = link?.formData?.lt_link_label?.value || '';
            },
            `Set Item Label [${idx}]`,
            this.errorHandler
          );
          safeAssign(
            () => {
              item.link =
                link?.formData?.lt_link_external?.value ??
                link?.formData?.lt_link_internal?.value?.url ??
                '';
            },
            `Set Item Link [${idx}]`,
            this.errorHandler
          );
          safeAssign(
            () => {
              item.title = link?.formData?.lt_link_title?.value || '';
            },
            `Set Item Title [${idx}]`,
            this.errorHandler
          );
          safeAssign(
            () => {
              item.leftIcon = this.getMultimediaLeftIcon(
                link?.formData?.lt_icon?.value?.identifier || ''
              );
            },
            `Set Item LeftIcon [${idx}]`,
            this.errorHandler
          );
          safeAssign(
            () => {
              item.rightIcons = this.getMultimediaRightIcons(link);
            },
            `Set Item RightIcons [${idx}]`,
            this.errorHandler
          );
          return item;
        });
      },
      'Set MultimediaList Items',
      this.errorHandler
    );

    return multimediaList;
  }

  getMultimediaRightIcons(link: CaasMultimediaListLink) {
    const linkType = link?.formData?.lt_link_type?.value?.identifier;
    if (!linkType) return [];

    switch (linkType) {
      case 'external':
        return this.getExternalIcons(link);
      case 'internal':
        return this.getInternalIcons(link);
      case 'contact':
        return this.getContactIcons(link);
      case 'download':
        return this.getDownloadIcons(link);
      default:
        return [];
    }
  }

  private getExternalIcons(link: CaasMultimediaListLink): MultimediaIcon[] {
    const href = link?.formData?.lt_link_external?.value ?? '';
    return [
      this.createIcon('arrow-up-right-from-square', href, '_blank', 'Öffnen'),
      this.createIcon('link', href, undefined, 'Link kopieren'),
    ];
  }

  private getInternalIcons(link: CaasMultimediaListLink): MultimediaIcon[] {
    const identifier =
      link?.formData?.lt_link_internal?.value?.identifier ?? '';
    const path =
      this.caasRoutesService.getSeoRouteByIdentifier(
        identifier,
        this.router.config
      )?.path ?? '/404';
    return [
      this.createIcon('right', path, '_self', 'Öffnen'),
      this.createIcon(
        'link',
        `${window.location.origin}/${path}`,
        undefined,
        'Link kopieren'
      ),
    ];
  }

  private getContactIcons(link: CaasMultimediaListLink): MultimediaIcon[] {
    const icons: MultimediaIcon[] = [];
    if (link?.formData?.lt_email_address?.value) {
      icons.push(
        this.createIcon(
          'envelope',
          `mailto:${link.formData.lt_email_address.value}`,
          undefined,
          'E-mail senden'
        )
      );
    }
    if (link?.formData?.lt_telephone?.value) {
      icons.push(
        this.createIcon(
          'phone',
          `tel:${link.formData.lt_telephone.value}`,
          undefined,
          'Anrufen'
        )
      );
    }
    return icons;
  }

  private getDownloadIcons(link: CaasMultimediaListLink): MultimediaIcon[] {
    const href = link?.formData?.lt_link_external?.value ?? '';
    const isBrandPortal = href.includes('https://www.hdi-brandportal.com');
    const icons: MultimediaIcon[] = [
      this.createIcon('eye', href, undefined, 'Ansicht'),
    ];
    if (!isBrandPortal) {
      icons.push(this.createIcon('down-to-line', href, undefined, 'Download'));
    }
    icons.push(this.createIcon('link', href, undefined, 'Link kopieren'));
    return icons;
  }

  private createIcon(
    type: string,
    href: string,
    target: string | undefined,
    title: string
  ): MultimediaIcon {
    return {
      type,
      href,
      target,
      tooltip: {
        id: crypto.randomUUID().toString(),
        description: `<span>${title}</span>`,
      },
      title,
    };
  }

  getMultimediaLeftIcon(leftIcon: string) {
    if (leftIcon.length) {
      if (leftIcon === 'file-pdf') {
        return 'file-pdf';
      } else {
        return leftIcon;
      }
    } else {
      return '';
    }
  }
  openModal(
    event: Event,
    icon: {
      type: string;
      href: string;
      target?: string;
      videoSrc?: string;
      thumbnailSrc?: string;
      videoId?: string;
    }
  ) {
    if (icon.videoSrc) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('openVideoModal', {
          detail: {
            videoSrc: icon.videoSrc,
            videoId: icon.videoId,
            thumbnailSrc: icon.thumbnailSrc,
          },
        });
        window.dispatchEvent(event);
      }
    }
  }

  async copyToClipboard(
    // Make the method async
    icon: MultimediaIcon,
    event: TouchEvent | MouseEvent | KeyboardEvent
  ) {
    event.preventDefault();
    event.stopPropagation();
    try {
      if (document.hasFocus()) {
        await navigator.clipboard.writeText(icon.href); // Await the promise
      } else {
        // Fallback for when document isn't focused
        await this.fallbackCopyToClipboard(icon.href);
      }

      // This code now only runs if the copy was successful
      this.clipboardTooltip = {
        id: icon?.tooltip?.id ?? '',
        description: '<span>URL kopiert</span>',
      };
      setTimeout(() => {
        this.clipboardTooltip = undefined;
      }, 500);
    } catch (err) {
      // This block will now correctly catch the rejection
      console.error('Failed to copy: ', err);
      this.fallbackCopyToClipboard(icon.href);
    }
  }

  private async fallbackCopyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      console.log('Text copied to clipboard successfully!');
    } catch (err) {
      console.error('Failed to copy text to clipboard: ', err);
    }
  }

  openTooltip(tooltip: Tooltip) {
    this.tooltip = tooltip;
  }

  closeTooltip() {
    this.tooltip = {
      id: '',
      description: '',
    };
  }

  getIconName(name: string): string {
    if (
      this.library.getIconDefinition('far' as IconPrefix, name as IconName) !==
      null
    ) {
      return name;
    } else {
      return 'user';
    }
  }

  ngOnDestroy() {
    this.modalVideoSubscription$.unsubscribe();
  }
}
